import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Sparkles, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable dialog that asks the user a small set of questions, then sends only
 * their answers to /api/ai/assist. The AI is instructed to use ONLY those facts.
 *
 * Props:
 *  - open, onOpenChange
 *  - title, description
 *  - questions: [{ key, label, placeholder }]
 *  - action: "generate_bullets" | "summary" | "improve"
 *  - role?: string (for bullets)
 *  - context?: string (for improve / summary continuity)
 *  - onResult: (text) => void
 */
export default function AIPromptDialog({
  open, onOpenChange,
  title, description, questions, action, role, context,
  onResult,
}) {
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  const reset = () => { setAnswers({}); setLoading(false); };

  const submit = async () => {
    const cleaned = Object.fromEntries(
      Object.entries(answers).map(([k, v]) => [k, (v || "").trim()]).filter(([, v]) => v)
    );
    if (Object.keys(cleaned).length === 0) {
      toast.error("Please answer at least one question");
      return;
    }
    setLoading(true);
    try {
      // Send facts as { questionLabel: answer } so the AI sees both sides
      const facts = {};
      for (const q of questions) {
        if (answers[q.key]) facts[q.label] = answers[q.key];
      }
      const { data } = await api.post("/ai/assist", { action, role, context, facts });
      if (data.text && data.text.startsWith("NEED:")) {
        toast.error(data.text.replace(/^NEED:\s*/, "Need more info: "));
        setLoading(false);
        return;
      }
      onResult(data.text);
      toast.success("AI suggestion ready");
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles className="w-4 h-4 text-blue-600" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 max-h-[55vh] overflow-y-auto pr-1">
          {questions.map((q) => (
            <div key={q.key}>
              <Label className="text-sm font-medium" htmlFor={`ai-q-${q.key}`}>{q.label}</Label>
              <Textarea
                id={`ai-q-${q.key}`}
                rows={2}
                value={answers[q.key] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                placeholder={q.placeholder}
                data-testid={`ai-question-${q.key}`}
                className="mt-1.5"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          The AI will use <span className="font-medium">only what you write here</span>. It won't
          invent details. Skip any question that doesn't apply.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={loading}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="ai-prompt-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Use my answers"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
